import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import host from "../../global.jsx";
import axios from "axios";
import LineChart from './components/LineChart/LineChart';
import ExpiringAccountsBubbleChart from "./components/BubbleChart/ExpiringAccountsBubbleChart";
import CustomBarChart from "./components/Barchart/CustomBarChart.jsx";
// import { useFormattedBarData } from "./hooks/useFormattedBarData.js";
import AccClsDonutChart from "./components/DonutChart/AccClsDonutChart.jsx"
import KPICard from "./components/KPICard/KPICard.jsx";
import useAccountActivity from './hooks/useAccountActivity';
import ReusableTable from './components/ReusableTable/ReusableTable';
import useClickOutside from "./hooks/useClickOutside.js";
import { FilterButton } from "./components/Filter/FilterButton";
import FilterPanel from "./components/Filter/FilterPanel";
import ColumnSelector from "./components/ColumnSelector/ColumnSelector.jsx";
import { jwtDecode } from "jwt-decode";

import { useDashboardAnalytics } from "./hooks/useDashboardAnalytics.js";
import { useOrganisations } from "./hooks/useOrganisations.js";
import { useInterviewCounts } from "./hooks/useInterviewCounts.js";
import { useGenieHireDashboard } from "./hooks/useGenieHireDashboard.js"
import { useActivityInsights } from "./hooks/useActivityInsights.js";
import { ALL_MONTHS } from "./utils/constants";

import {
  useMonthlyChartData,
  useDailyAccountCreationData,
  useExpiringSegments,
} from "./hooks/useChartData";

import { formatDate, formatLocalDate } from "./utils/dateHelpers";
import { getDaysRemaining, getDaysRemainingColor, thirtyDaysFromNow } from "./utils/dashboardHelpers";
import { COLORS } from "./utils/constants";

import {
  SlidersHorizontal,
  X,
  Layout,
  ChevronDown,
  ChevronUp,
  XCircleIcon,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import {
  TextField,
  InputAdornment,
  ThemeProvider,
  createTheme,
  Skeleton,
} from "@mui/material";
import Swal from "sweetalert2";
import DateRangePicker from "../DatePicker";
import "../../App.css";
import Loader from "../Loader";

const DashboardContent = () => {
  const navigate = useNavigate();
  const accessToken = sessionStorage.getItem("accessToken");
  const decoded = jwtDecode(accessToken);
  // const organizationId = decoded.organization_id;

  const [orgIdMap, setOrgIdMap] = useState({});
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [chartDateRange, setChartDateRange] = useState([null, null]);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [chartIsFilterOpen, setChartIsFilterOpen] = useState(false);
  const [tempDateRange, setTempDateRange] = useState(dateRange);
  const [chartTempDateRange, setChartTempDateRange] = useState(chartDateRange);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [isBarModalOpen, setIsBarModalOpen] = useState(false);

  const [jobDateRange, setJobDateRange] = useState([null, null]);
  const [jobTempDateRange, setJobTempDateRange] = useState([null, null]);
  const [jobIsFilterOpen, setJobIsFilterOpen] = useState(false);

  const jobFilterRef = useRef(null);
  const jobButtonRef = useRef(null);
  const [selectedOrg, setSelectedOrg] = useState("All Organizations");

  const buttonRef = useRef(null);
  const chartFilterRef = useRef(null);
  const chartButtonRef = useRef(null);
  const [showOrgWarning, setShowOrgWarning] = useState(false);
  const [selectedDatePeriod, setSelectedDatePeriod] = useState("weekly");
  const [showMostUsed, setShowMostUsed] = useState(true);

  const [activityDateRange, setActivityDateRange] = useState([null, null]);
  const [activityTempDateRange, setActivityTempDateRange] = useState([
    null,
    null,
  ]);
  const [activityIsFilterOpen, setActivityIsFilterOpen] = useState(false);
  const [activitySelectedDatePeriod, setActivitySelectedDatePeriod] =
    useState("weekly");
  const [chartSelectedDatePeriod, setChartSelectedDatePeriod] = useState("custom");
  const [dashboardSelectedDatePeriod, setDashboardSelectedDatePeriod] = useState("custom");
  const activityFilterRef = useRef(null);
  const activityButtonRef = useRef(null);
  const [isOrgDropdownOpen, setIsOrgDropdownOpen] = useState(false);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [isActivityDateDropdownOpen, setIsActivityDateDropdownOpen] =
    useState(false);
  const [isDistDateDropdownOpen, setIsDistDateDropdownOpen] = useState(false);
  const [isDashboardDateDropdownOpen, setIsDashboardDateDropdownOpen] =
    useState(false);
  const [selectedFeature, setSelectedFeature] = useState("All");
  const [isFeatureDropdownOpen, setIsFeatureDropdownOpen] = useState(false);

  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    adminName: true,
    total: true,
    mostUsedFeature: true,
    mostUsedCount: true,
  });

  const dashboardFilterRef = useRef(null);
  const [featureOpen, setFeatureOpen] = useState(false);
  const [orgOpen, setOrgOpen] = useState(false);

  const columnOptions = [
    { key: "name", label: "Account Name" },
    { key: "adminName", label: "Admin Name" },
    { key: "total", label: "Activity Count" },
    { key: "mostUsedFeature", label: "Most Used Feature" },
    { key: "mostUsedCount", label: "Feature Count" },
  ];

  const { analyticsData, loading: analyticsLoading, error: analyticsError } = useDashboardAnalytics();

  const { data: allOrgs } = useOrganisations();

  const expiringAdmins = useMemo(() => {
    if (!allOrgs) return [];
    const today = new Date();
    const thirtyDays = new Date();
    thirtyDays.setDate(today.getDate() + 30);

    return allOrgs.filter((admin) => {
      if (!admin.end_date || admin.account_type === "Testing") return false;
      const endDate = new Date(admin.end_date);
      return endDate >= today && endDate <= thirtyDays;
    });
  }, [allOrgs]);

  const { interviewCount, scheduledTimes } = useInterviewCounts();

  const {
    jobPostings,
    interviewSchedules,
    resumeParsed,
    loading: genieLoading,
  } = useGenieHireDashboard(jobDateRange, selectedOrg);

  const { monthlyBarData, isDefault: isChartDefault } = useMonthlyChartData(analyticsData, chartDateRange);
  const dailyData = useDailyAccountCreationData(analyticsData, chartDateRange);
  const expiringSegments = useExpiringSegments(expiringAdmins);

  useClickOutside([dashboardFilterRef, buttonRef], () => {
    setIsFilterOpen(false);
    setIsDashboardDateDropdownOpen(false);
  });
  useClickOutside([chartFilterRef, chartButtonRef], () => {
    setIsDistDateDropdownOpen(false);
    setChartIsFilterOpen(false);
  });
  useClickOutside([activityFilterRef, activityButtonRef], () => {
    setIsActivityDateDropdownOpen(false)
    setActivityIsFilterOpen(false)
  });
  useClickOutside([jobFilterRef, jobButtonRef], () => {
    setIsDateDropdownOpen(false);
    setIsOrgDropdownOpen(false);
  });

  useClickOutside([jobFilterRef, jobButtonRef], () => {
    setJobIsFilterOpen(false);
    setIsOrgDropdownOpen(false);
    setIsDateDropdownOpen(false);
    setIsFeatureDropdownOpen(false);
  });

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isModalOpen]);

  const {
    jobPostings: activityJobPostings,
    interviewSchedules: activityInterviewSchedules,
    perOrgResumes: activityPerOrgResumes,
    availableOrgs,
    loading: activityLoading,
    error: activityError,
  } = useActivityInsights(activityDateRange);

  const { mostUsed = [], leastUsed = [] } = useAccountActivity({
    jobPostings: activityJobPostings,
    interviewSchedules: activityInterviewSchedules,
    perOrgResumes: activityPerOrgResumes,
    dateRange: activityDateRange,
  });

  // useClickOutside([columnSelectorRef], () => setShowColumnSelector(false));

  useEffect(() => {
    setTempDateRange(dateRange);
  }, [dateRange]);

  useEffect(() => {
    setChartTempDateRange(chartDateRange);
  }, [chartDateRange]);

  useEffect(() => {
    setJobTempDateRange(jobDateRange);
  }, [jobDateRange]);

  useEffect(() => {
    setActivityTempDateRange(activityDateRange);
  }, [activityDateRange]);

  useEffect(() => {
    if (allOrgs && allOrgs.length > 0) {
      const map = {};
      allOrgs.forEach((org) => {
        if (org.organization_name && org.organization_id) {
          map[org.organization_name] = org.organization_id;
        }
      });
      setOrgIdMap(map);
    }
  }, [allOrgs]);

  useEffect(() => {
    if (selectedOrg === "All Organizations") {
      setSelectedOrgId(null);
    } else {
      setSelectedOrgId(orgIdMap[selectedOrg] || null);
    }
  }, [selectedOrg, orgIdMap]);

  const activityActiveFilters = [];
  if (activitySelectedDatePeriod !== "weekly" || activityDateRange[0] || activityDateRange[1]) {
    activityActiveFilters.push({
      label: `Date: ${activitySelectedDatePeriod === "custom"
        ? (activityDateRange[0] ? `${formatDate(activityDateRange[0])} - ${formatDate(activityDateRange[1])}` : "Custom")
        : activitySelectedDatePeriod.charAt(0).toUpperCase() + activitySelectedDatePeriod.slice(1)}`,
      onRemove: () => {
        setActivityTempDateRange([null, null]);
        setActivityDateRange([null, null]);
        setActivitySelectedDatePeriod("weekly");
      },
    });
  }
  if (!showMostUsed) {  // Default is true ("Most"), so active if false ("Least")
    activityActiveFilters.push({
      label: `View: Least Used`,
      onRemove: () => setShowMostUsed(true),
    });
  }
  const activityFilterCount = activityActiveFilters.length;

  const jobActiveFilters = [];
  if (selectedDatePeriod !== "weekly" || jobDateRange[0] || jobDateRange[1]) {
    jobActiveFilters.push({
      label: `Date: ${selectedDatePeriod === "weekly"
        ? (jobDateRange[0] ? `${formatDate(jobDateRange[0])} - ${formatDate(jobDateRange[1])}` : "Last 7 Days")
        : selectedDatePeriod.charAt(0).toUpperCase() + selectedDatePeriod.slice(1)}`,
      onRemove: () => {
        setJobTempDateRange([null, null]);
        setJobDateRange([null, null]);
        setSelectedDatePeriod("weekly");
      },
    });
  }
  if (selectedFeature !== "All") {
    jobActiveFilters.push({
      label: `Feature: ${selectedFeature}`,
      onRemove: () => setSelectedFeature("All"),
    });
  }
  if (selectedOrg !== "All Organizations") {
    jobActiveFilters.push({
      label: `Org: ${selectedOrg.length > 15 ? `${selectedOrg.slice(0, 12)}...` : selectedOrg}`,
      onRemove: () => setSelectedOrg("All Organizations"),
    });
  }
  const jobFilterCount = jobActiveFilters.length;

  const dashboardActiveFilters = [];
  if (dashboardSelectedDatePeriod !== "custom" || dateRange[0] || dateRange[1]) {
    dashboardActiveFilters.push({
      label: `Date: ${dashboardSelectedDatePeriod === "custom"
        ? (dateRange[0] ? `${formatDate(dateRange[0])} - ${formatDate(dateRange[1])}` : "Custom")
        : dashboardSelectedDatePeriod.charAt(0).toUpperCase() + dashboardSelectedDatePeriod.slice(1)}`,
      onRemove: () => {
        setTempDateRange([null, null]);
        setDateRange([null, null]);
        setDashboardSelectedDatePeriod("custom");
      },
    });
  }
  const dashboardFilterCount = dashboardActiveFilters.length;

  const chartActiveFilters = [];
  if (chartSelectedDatePeriod !== "custom" || chartDateRange[0] || chartDateRange[1]) {
    chartActiveFilters.push({
      label: `Date: ${chartSelectedDatePeriod === "custom"
        ? (chartDateRange[0] ? `${formatDate(chartDateRange[0])} - ${formatDate(chartDateRange[1])}` : "Custom")
        : chartSelectedDatePeriod.charAt(0).toUpperCase() + chartSelectedDatePeriod.slice(1)}`,
      onRemove: () => {
        setChartTempDateRange([null, null]);
        setChartDateRange([null, null]);
        setChartSelectedDatePeriod("custom");
      },
    });
  }
  const chartFilterCount = chartActiveFilters.length;

  const getFilteredData = useMemo(() => {
    if (!analyticsData) return analyticsData;

    const [startDate, endDate] = dateRange;

    if (!startDate && !endDate) {
      const active_accounts = analyticsData?.active_accounts || [];
      const trial_accounts = active_accounts.filter(
        (item) => item.account_type === "Trial"
      );
      const paid_accounts = active_accounts.filter(
        (item) => item.account_type === "Paid"
      );
      const upgrade_timestamps = analyticsData?.upgrade_timestamps || [];

      return {
        ...analyticsData,
        active_accounts_count: active_accounts.length,
        trial_accounts_count: trial_accounts.length,
        paid_accounts_count: paid_accounts.length,
        upgrade_timestamp_count: upgrade_timestamps.length,
      };
    }

    const filterByDate = (items, dateField) => {
      return items.filter((item) => {
        const itemDate = new Date(item[dateField]);
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;

        if (start && end) {
          return itemDate >= start && itemDate <= end;
        } else if (start) {
          return itemDate >= start;
        } else if (end) {
          return itemDate <= end;
        }
        return true;
      });
    };

    const active_accounts = filterByDate(
      analyticsData?.active_accounts || [],
      "created_at"
    );
    const trial_accounts = active_accounts.filter(
      (item) => item.account_type === "Trial"
    );
    const paid_accounts = active_accounts.filter(
      (item) => item.account_type === "Paid"
    );
    const upgrade_timestamps = filterByDate(
      analyticsData?.upgrade_timestamps || [],
      "upgrade_timestamp"
    );

    return {
      ...analyticsData,
      active_accounts_count: active_accounts.length,
      trial_accounts_count: trial_accounts.length,
      paid_accounts_count: paid_accounts.length,
      upgrade_timestamp_count: upgrade_timestamps.length,
    };
  }, [analyticsData, dateRange]);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  const filteredAdmins = useMemo(() => {
    if (!selectedSegment) return expiringAdmins;

    const now = new Date();
    if (selectedSegment === "0-7 Days") {
      const in7Days = new Date();
      in7Days.setDate(now.getDate() + 7);
      return expiringAdmins.filter(
        (admin) => new Date(admin.end_date) <= in7Days
      );
    } else if (selectedSegment === "8-15 Days") {
      const in7Days = new Date();
      in7Days.setDate(now.getDate() + 7);
      const in15Days = new Date();
      in15Days.setDate(now.getDate() + 15);
      return expiringAdmins.filter((admin) => {
        const end = new Date(admin.end_date);
        return end > in7Days && end <= in15Days;
      });
    } else if (selectedSegment === "16-30 Days") {
      const in15Days = new Date();
      in15Days.setDate(now.getDate() + 15);
      const in30Days = new Date();
      in30Days.setDate(now.getDate() + 30);
      return expiringAdmins.filter((admin) => {
        const end = new Date(admin.end_date);
        return end > in15Days && end <= in30Days;
      });
    }
    return expiringAdmins;
  }, [expiringAdmins, selectedSegment]);

  const filteredExpiringAdminsCount = useMemo(() => {
    // if (!expiringAdmins) return 0;
    if (!expiringAdmins?.length) return 0;

    const [startDate, endDate] = dateRange;

    if (!startDate && !endDate) {
      const now = new Date();
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(now.getDate() + 30);

      return expiringAdmins.filter((admin) => {
        if (!admin.end_date) return false;
        const endDate = new Date(admin.end_date);
        return endDate >= now && endDate <= thirtyDaysFromNow;
      }).length;
    }

    return expiringAdmins.filter((admin) => {
      if (!admin.end_date) return false;
      const adminEndDate = new Date(admin.end_date);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;

      if (start && end) {
        return adminEndDate >= start && adminEndDate <= end;
      } else if (start) {
        return adminEndDate >= start;
      } else if (end) {
        return adminEndDate <= end;
      }
      return true;
    }).length;
  }, [expiringAdmins, dateRange]);

  const filteredInterviewCount = useMemo(() => {
    // if (!scheduledTimes || scheduledTimes.length === 0) return 0;
    // if (!scheduledTimes?.length) return 0;
    if (!Array.isArray(scheduledTimes) || scheduledTimes.length === 0) return 0;

    const [startDate, endDate] = dateRange;

    // No filter applied → show total
    if (!startDate && !endDate) {
      return interviewCount;
    }

    const format = (d) => d ? formatLocalDate(d) : null;
    const filterStart = startDate ? format(startDate) : null;
    const filterEnd = endDate ? format(endDate) : null;

    return scheduledTimes.filter((timeStr) => {
      if (!timeStr) return false;
      const interviewDateStr = formatLocalDate(timeStr); // ← normalize!

      if (filterStart && filterEnd) {
        return interviewDateStr >= filterStart && interviewDateStr <= filterEnd;
      }
      if (filterStart) return interviewDateStr >= filterStart;
      if (filterEnd) return interviewDateStr <= filterEnd;
      return true;
    }).length;
  }, [scheduledTimes, dateRange, interviewCount]);

  const COLORS = [
    "#ad3bddff",
    "#B282F9",
    "#9500D3",
    "#53b025",
    "#2729a1",
    "#c71818",
  ];

  const handleBarClick = (event) => {
    // if (isDefault) return;
    if (isChartDefault) return;
    setIsBarModalOpen(true);
  };

  const jobDailyData = useMemo(() => {
    // if (!jobPostings.length) return [];
    if (!jobPostings?.length) return [];

    let filteredJobs = jobPostings;
    if (selectedOrg !== "All Organizations") {
      filteredJobs = jobPostings.filter(
        (job) => job.OrganizationName === selectedOrg
      );
    }

    let start = jobDateRange[0]
      ? new Date(jobDateRange[0])
      : new Date(new Date().setDate(new Date().getDate() - 7));
    let end = jobDateRange[1] ? new Date(jobDateRange[1]) : new Date();
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    const dailyCounts = {};
    filteredJobs.forEach((job) => {
      // Ensure postingDate is treated as a local date
      const dateKey = formatLocalDate(new Date(job.postingDate));
      dailyCounts[dateKey] = (dailyCounts[dateKey] || 0) + 1;
    });

    const allDates = [];
    let currentDate = new Date(start);
    while (currentDate <= end) {
      const dateKey = formatLocalDate(currentDate);
      allDates.push({
        date: dateKey,
        count: dailyCounts[dateKey] || 0,
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return allDates;
  }, [jobPostings, jobDateRange, selectedOrg]);

  const interviewDailyData = useMemo(() => {
    // if (!interviewSchedules.length) return [];
    if (!interviewSchedules?.length) return [];

    let filteredSchedules = interviewSchedules;
    if (selectedOrg !== "All Organizations") {
      filteredSchedules = interviewSchedules.filter(
        (s) => s.OrganizationName === selectedOrg
      );
    }

    let start = jobDateRange[0]
      ? new Date(jobDateRange[0])
      : new Date(new Date().setDate(new Date().getDate() - 7));
    let end = jobDateRange[1] ? new Date(jobDateRange[1]) : new Date();
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    const dailyCounts = {};
    filteredSchedules.forEach((schedule) => {
      for (const [dateKey, count] of Object.entries(
        schedule.interview_counts || {}
      )) {
        const formattedDateKey = formatLocalDate(new Date(dateKey));
        dailyCounts[formattedDateKey] =
          (dailyCounts[formattedDateKey] || 0) + count;
      }
    });

    const allDates = [];
    let currentDate = new Date(start);
    while (currentDate <= end) {
      const dateKey = formatLocalDate(currentDate);
      allDates.push({
        date: dateKey,
        count: dailyCounts[dateKey] || 0,
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return allDates;
  }, [interviewSchedules, jobDateRange, selectedOrg]);

  const resumeDailyData = useMemo(() => {
    // if (!resumeParsedData.length) return [];
    if (!resumeParsed?.length) return [];

    let start = jobDateRange[0]
      ? new Date(jobDateRange[0])
      : new Date(new Date().setDate(new Date().getDate() - 7));
    let end = jobDateRange[1] ? new Date(jobDateRange[1]) : new Date();
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    const dailyCounts = {};
    resumeParsed.forEach((dateStr) => {
      const formattedDateKey = formatLocalDate(new Date(dateStr));
      dailyCounts[formattedDateKey] = (dailyCounts[formattedDateKey] || 0) + 1;
    });

    const allDates = [];
    let currentDate = new Date(start);
    while (currentDate <= end) {
      const dateKey = formatLocalDate(currentDate);
      allDates.push({
        date: dateKey,
        count: dailyCounts[dateKey] || 0,
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return allDates;
  }, [resumeParsed, jobDateRange]);

  // Dynamically filter available organizations based on jobDateRange
  const availableOrgsForJobChart = useMemo(() => {
    const orgSet = new Set();

    // Calculate the date range
    let start = jobDateRange[0]
      ? new Date(jobDateRange[0])
      : new Date(new Date().setDate(new Date().getDate() - 7));
    let end = jobDateRange[1] ? new Date(jobDateRange[1]) : new Date();
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    // Filter job postings by date range and collect orgs
    jobPostings?.forEach((job) => {
      const postingDate = new Date(job.postingDate);
      if (postingDate >= start && postingDate <= end && job.OrganizationName) {
        orgSet.add(job.OrganizationName);
      }
    });

    // Filter interview schedules by date range and collect orgs
    interviewSchedules?.forEach((schedule) => {
      if (schedule.OrganizationName && schedule.interview_counts) {
        for (const dateKey of Object.keys(schedule.interview_counts)) {
          const interviewDate = new Date(dateKey);
          if (interviewDate >= start && interviewDate <= end) {
            orgSet.add(schedule.OrganizationName);
            break; // Found at least one interview in range
          }
        }
      }
    });

    const unique = Array.from(orgSet).sort((a, b) => a.localeCompare(b));
    return unique;
  }, [jobPostings, interviewSchedules, resumeParsed, jobDateRange]);

  const accountActivity = useMemo(() => {
    const start = activityDateRange[0]
      ? new Date(activityDateRange[0])
      : new Date(new Date().setDate(new Date().getDate() - 7));
    const end = activityDateRange[1]
      ? new Date(activityDateRange[1])
      : new Date();
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    const activityCounts = {};

    const orgAdminMap = {};
    const filteredJobPostings = activityJobPostings.filter((job) => {
      const postingDate = new Date(job.postingDate);
      return postingDate >= start && postingDate <= end;
    });

    filteredJobPostings.forEach((job) => {
      const orgName = job.OrganizationName || "Unknown";
      if (job.org_admin_name) {
        orgAdminMap[orgName] = job.org_admin_name;
      }
    });

    filteredJobPostings.forEach((job) => {
      const orgName = job.OrganizationName || "Unknown";
      if (!activityCounts[orgName]) {
        activityCounts[orgName] = {
          jobPostings: 0,
          interviews: 0,
          resumes: 0,
          total: 0,
        };
      }
      activityCounts[orgName].jobPostings += 1;
      activityCounts[orgName].total += 1;
    });

    const filteredInterviewSchedules = activityInterviewSchedules
      .map((schedule) => {
        const filteredCounts = {};
        for (const [dateKey, count] of Object.entries(
          schedule.interview_counts || {}
        )) {
          const interviewDate = new Date(dateKey);
          if (interviewDate >= start && interviewDate <= end) {
            filteredCounts[dateKey] = count;
          }
        }
        return {
          ...schedule,
          interview_counts: filteredCounts,
        };
      })
      .filter((schedule) => Object.keys(schedule.interview_counts).length > 0);

    filteredInterviewSchedules.forEach((schedule) => {
      for (const [dateKey, count] of Object.entries(
        schedule.interview_counts
      )) {
        const orgName = schedule.OrganizationName || "Unknown";
        if (!activityCounts[orgName]) {
          activityCounts[orgName] = {
            jobPostings: 0,
            interviews: 0,
            resumes: 0,
            total: 0,
          };
        }
        activityCounts[orgName].interviews += count;
        activityCounts[orgName].total += count;
      }
    });

    const filteredPerOrgResumes = activityPerOrgResumes
      .map((org) => {
        const filteredCounts = {};
        for (const [dateKey, count] of Object.entries(
          org.resume_parsed_counts || {}
        )) {
          const resumeDate = new Date(dateKey);
          if (resumeDate >= start && resumeDate <= end) {
            filteredCounts[dateKey] = count;
          }
        }
        return {
          ...org,
          resume_parsed_counts: filteredCounts,
        };
      })
      .filter((org) => Object.keys(org.resume_parsed_counts).length > 0);

    filteredPerOrgResumes.forEach((org) => {
      for (const [dateKey, count] of Object.entries(org.resume_parsed_counts)) {
        const orgName = org.OrganizationName || "Unknown";
        if (!activityCounts[orgName]) {
          activityCounts[orgName] = {
            jobPostings: 0,
            interviews: 0,
            resumes: 0,
            total: 0,
          };
        }
        activityCounts[orgName].resumes += count;
        activityCounts[orgName].total += count;
      }
    });

    const getMostUsedFeature = (counts) => {
      const { jobPostings, interviews, resumes } = counts;
      const max = Math.max(jobPostings, interviews, resumes);
      if (max === 0) return "None";
      if (max === jobPostings) return "Job Postings";
      if (max === interviews) return "Interview Scheduled";
      return "Resume Parsed";
    };

    const getMostUsedCount = (counts) => {
      return Math.max(counts.jobPostings, counts.interviews, counts.resumes);
    };

    const activityArray = Object.entries(activityCounts)
      .filter(
        ([name]) =>
          name !== "All Organizations" && activityCounts[name].total > 0
      )
      .map(([name, counts]) => ({
        name,
        ...counts,
        mostUsedFeature: getMostUsedFeature(counts),
        mostUsedCount: getMostUsedCount(counts),
        adminName: orgAdminMap[name] || "N/A",
      }));

    activityArray.sort((a, b) => b.total - a.total);

    const totalAccounts = activityArray.length;
    let mostUsed, leastUsed;

    if (totalAccounts <= 5) {
      mostUsed = activityArray.slice(0, 5);
      leastUsed = [];
    } else {
      mostUsed = activityArray.slice(0, Math.min(5, totalAccounts));
      leastUsed = activityArray.slice(
        Math.min(5, totalAccounts),
        totalAccounts
      );
      leastUsed.sort((a, b) => a.total - b.total);
      leastUsed = leastUsed.slice(0, Math.min(5, leastUsed.length));
    }

    return { mostUsed, leastUsed };
  }, [
    activityJobPostings,
    activityInterviewSchedules,
    activityPerOrgResumes,
    activityDateRange,
  ]);

  const chartTheme = createTheme({
    components: {
      MuiChartsAxis: {
        styleOverrides: {
          line: {
            stroke: "#ffffff",
          },
          tick: {
            stroke: "#ffffff",
          },
          tickLabel: {
            fill: "#ffffff",
            fontWeight: 300,
          },
          root: {
            color: "#ffffff",
          },
        },
      },
      MuiChartsGrid: {
        styleOverrides: {
          line: {
            stroke: "rgba(255, 255, 255, 0.3)",
          },
        },
      },
    },
  });

  // Skeleton UI for the dashboard
  const renderSkeleton = () => (
    <div className="min-h-screen p-4 space-y-10 text-[#D3B4FF] overflow-x-hidden overflow-y-auto overscroll-none">
      {/* Header Skeleton */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <Skeleton
          variant="text"
          width={250}
          height={40}
          sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
        />
        <Skeleton
          variant="rectangular"
          width={120}
          height={40}
          sx={{ bgcolor: "rgba(255, 255, 255, 0.1)", borderRadius: "9999px" }}
        />
      </div>

      {/* Cards Skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
        {Array(5)
          .fill()
          .map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              width="100%"
              height={110}
              sx={{
                bgcolor: "rgba(255, 255, 255, 0.1)",
                borderRadius: "10px",
                maxWidth: "250px",
              }}
            />
          ))}
      </div>

      {/* Charts and Table Skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-1 gap-12">
        <div className="flex flex-col gap-8">
          <div className="flex flex-row gap-8">
            {/* Bar Chart Skeleton */}
            <Skeleton
              variant="rectangular"
              width={700}
              height={353}
              sx={{ bgcolor: "rgba(255, 255, 255, 0.1)", borderRadius: "6px" }}
            />
            {/* Line Chart Skeleton */}
            <Skeleton
              variant="rectangular"
              width={700}
              height={353}
              sx={{ bgcolor: "rgba(255, 255, 255, 0.1)", borderRadius: "6px" }}
            />
          </div>
          <div className="flex flex-row gap-8">
            {/* Table Skeleton */}
            <div className="w-[840px]">
              <Skeleton
                variant="rectangular"
                width="100%"
                height={40}
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  borderRadius: "6px",
                  mb: 2,
                }}
              />
              <Skeleton
                variant="rectangular"
                width="100%"
                height={300}
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  borderRadius: "6px",
                }}
              />
            </div>
            {/*  Chart Skeleton */}
            <Skeleton
              variant="rectangular"
              width={480}
              height={353}
              sx={{ bgcolor: "rgba(255, 255, 255, 0.1)", borderRadius: "6px" }}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const loading = analyticsLoading;
  // const loading = analyticsLoading || activityLoading;
  if (loading) {
    return renderSkeleton();
  }
  // if (error) return <div className="p-6 text-[#D3B4FF]">Error: {error}</div>;
  if (analyticsError) return <div className="p-6 text-[#D3B4FF]">Error: {analyticsError}</div>;
  if (!analyticsData)
    return <div className="p-6 text-[#D3B4FF]">No data available.</div>;

  return (
    <div className="space-y-10 text-[#D3B4FF]">
      <div
        style={{
          border: "1px solid transparent",
          padding: "16px",
          borderRadius: "8px",
        }}
      >
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <h1 className="text-3xl font-normal text-[#D3B4FF]">
            Dashboard Overview
          </h1>
          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
            <div className="relative z-[2]">
              <div ref={buttonRef}>
                <FilterButton
                  label="Filter"
                  filterCount={dashboardFilterCount}
                  onClick={() => setIsFilterOpen(prev => !prev)}
                  onClear={() => {
                    setTempDateRange([null, null]);
                    setDateRange([null, null]);
                    setDashboardSelectedDatePeriod("custom");
                  }}
                />
              </div>

              <FilterPanel
                ref={dashboardFilterRef}
                isOpen={isFilterOpen}
                onClose={() => setIsFilterOpen(false)}
                activeFilters={dashboardActiveFilters}
                selectedPeriod={dashboardSelectedDatePeriod}
                onPeriodChange={setDashboardSelectedDatePeriod}
                tempDateRange={tempDateRange}
                onTempDateChange={setTempDateRange}
                dropdownOpen={isDashboardDateDropdownOpen}
                setDropdownOpen={setIsDashboardDateDropdownOpen}
                onApply={() => {
                  setDateRange(tempDateRange);
                  setIsFilterOpen(false);
                }}
                onReset={() => {
                  setTempDateRange([null, null]);
                  setDateRange([null, null]);
                  setDashboardSelectedDatePeriod("custom");
                }}
                position="bottom-right"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 -mb-6">
          <div className="group relative overflow-hidden hover:scale-105 transition-transform duration-300">
            <KPICard title="Active Accounts" value={getFilteredData?.active_accounts_count} trend="up" color="green" />
          </div>

          <div className="group relative overflow-hidden hover:scale-105 transition-transform duration-300">
            <KPICard title="Trial Accounts" value={getFilteredData?.trial_accounts_count} trend="up" color="blue" />
          </div>

          <div className="group relative overflow-hidden hover:scale-105 transition-transform duration-300">
            <KPICard title="Paid Accounts" value={getFilteredData?.paid_accounts_count} trend="up" color="indigo" />
          </div>

          <div className="group relative overflow-hidden hover:scale-105 transition-transform duration-300">
            <KPICard title="Accounts Expiring Soon" value={filteredExpiringAdminsCount} trend="down" color="yellow" />
          </div>

          <div className="group relative overflow-hidden hover:scale-105 transition-transform duration-300">
            <KPICard title="Total Interviews" value={filteredInterviewCount} trend="up" color="pink" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-1 gap-12">
        <div className="flex flex-col gap-8">
          <div className="flex flex-col 2xl:flex-row gap-8">
            <div
              className="glass-effect p-4 rounded-2xl relative z-[10] w-full md:w-[700px] flex-shrink-0"
              style={{
                width: "700px",
                height: "353px",
                borderRadius: "6px",
                background:
                  "linear-gradient(252.82deg, rgba(217, 217, 217, 0.025) 25.41%, rgba(115, 108, 207, 0.005) 115.2%)",
                backdropFilter: "blur(75px)",
              }}
            >
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-md font-medium text-[#D3B4FF]">
                  Trial and Paid Accounts Distribution
                </h3>

                <div className="relative">
                  <div ref={chartButtonRef}>
                    <FilterButton
                      filterCount={chartFilterCount}
                      onClick={() => setChartIsFilterOpen(!chartIsFilterOpen)}
                      onClear={() => {
                        setChartTempDateRange([null, null]);
                        setChartDateRange([null, null]);
                        setChartSelectedDatePeriod("custom");
                      }}
                    />
                  </div>

                  <FilterPanel
                    isOpen={chartIsFilterOpen}
                    ref={chartFilterRef}
                    onClose={() => setChartIsFilterOpen(false)}
                    activeFilters={chartActiveFilters}
                    selectedPeriod={chartSelectedDatePeriod}
                    onPeriodChange={setChartSelectedDatePeriod}
                    tempDateRange={chartTempDateRange}
                    onTempDateChange={setChartTempDateRange}
                    onApply={() => {
                      setChartDateRange(chartTempDateRange);
                      setChartIsFilterOpen(false);
                    }}
                    onReset={() => {
                      setChartTempDateRange([null, null]);
                      setChartDateRange([null, null]);
                      setChartSelectedDatePeriod("custom");
                    }}
                    position="bottom-right"
                    dropdownOpen={isDistDateDropdownOpen}
                    setDropdownOpen={setIsDistDateDropdownOpen}
                  />
                </div>
              </div>

              {/* Bar Chart */}
              <div className="mt-4 h-[260px] cursor-pointer">
                <CustomBarChart
                  data={monthlyBarData}
                  xKey="month"
                  bars={[
                    { dataKey: "Trial", fill: "#ad3bddff", name: "Trial" },
                    { dataKey: "Paid", fill: "#B282F9", name: "Paid" },
                  ]}
                  height={260}
                  chartTheme={chartTheme}
                  tooltipLabelFormatter={(label) => label}
                  onBarClick={(entry) => {
                    if (isChartDefault) return;

                    const monthLabel = entry?.payload?.month || entry?.month;
                    if (!monthLabel) return;

                    const parts = monthLabel.trim().split(" ");
                    const monthName = parts[0];
                    const yearFromLabel = parts[1] ? parseInt(parts[1]) : null;

                    const monthIndex = ALL_MONTHS.indexOf(monthName);
                    if (monthIndex === -1) return;

                    const targetYear = yearFromLabel || new Date().getFullYear();

                    const startDate = new Date(targetYear, monthIndex, 1);
                    const endDate = new Date(targetYear, monthIndex + 1, 0);
                    endDate.setHours(23, 59, 59, 999);

                    setChartDateRange([startDate, endDate]);
                    setIsBarModalOpen(true);
                  }}
                />
              </div>

              {/* Legend */}
              <div className="flex justify-center -mt-6 space-x-8 text-[#D3B4FF] text-sm font-semibold">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-[#ad3bddff]" />
                  <span>Trial Accounts</span>
                </div>

                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-[#B282F9]" />
                  <span>Paid Accounts</span>
                </div>
              </div>
            </div>

            <div className="w-full flex justify-center md:justify-start">
              <AccClsDonutChart />
            </div>
          </div>

          <div className="flex flex-row gap-8 -mb-1">
            <div className="flex flex-col 2xl:flex-row gap-8 z-[]">
              {/* Account Activity Insights (Table) - Now on the Right */}
              <div
                className="glass-effect p-4 rounded-2xl border border-yellow-500/30 w-full md:w-[710px] flex-shrink-0 min-h-96"
                style={{
                  borderRadius: "6px",
                  background: "linear-gradient(252.82deg, rgba(217, 217, 217, 0.025) 25.41%, rgba(115, 108, 207, 0.005) 115.2%)",
                  backdropFilter: "blur(75px)",
                  border: "0px solid",
                  position: "relative",
                }}
              >
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-md font-small text-[#D3B4FF]">
                    Account Activity Insights
                  </h2>
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <div ref={activityButtonRef}>
                        <FilterButton
                          filterCount={activityFilterCount}
                          onClick={() => setActivityIsFilterOpen(!activityIsFilterOpen)}
                          onClear={() => {
                            setActivityTempDateRange([null, null]);
                            setActivityDateRange([null, null]);
                            setActivitySelectedDatePeriod("weekly");
                            setShowMostUsed(true);
                          }}
                        />
                      </div>

                      <FilterPanel
                        ref={activityFilterRef}
                        isOpen={activityIsFilterOpen}
                        onClose={() => setActivityIsFilterOpen(false)}
                        activeFilters={activityActiveFilters}
                        selectedPeriod={activitySelectedDatePeriod}
                        onPeriodChange={setActivitySelectedDatePeriod}
                        tempDateRange={activityTempDateRange}
                        onTempDateChange={setActivityTempDateRange}
                        onApply={() => {
                          setActivityDateRange(activityTempDateRange);
                          setActivityIsFilterOpen(false);
                        }}
                        onReset={() => {
                          setActivityTempDateRange([null, null]);
                          setActivityDateRange([null, null]);
                          setActivitySelectedDatePeriod("weekly");
                          setShowMostUsed(true);
                        }}
                        extraContent={
                          <div className="px-4 mb-4">
                            <div className="flex items-center justify-between">
                              <span className="text-[#CCCCCC] text-sm">Least Used</span>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={showMostUsed}
                                  onChange={() => setShowMostUsed(!showMostUsed)}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-[#5A4479] rounded-full peer peer-checked:bg-[#ad3bddff]"></div>
                                <div className="absolute left-1 top-1 w-4 h-4 bg-[#D3B4FF] rounded-full transition-transform peer-checked:translate-x-5"></div>
                              </label>
                              <span className="text-[#CCCCCC] text-sm">Most Used</span>
                            </div>
                          </div>
                        }
                        position="bottom-right"
                        dropdownOpen={isActivityDateDropdownOpen}
                        setDropdownOpen={setIsActivityDateDropdownOpen}
                      />
                    </div>
                    <ColumnSelector
                      visibleColumns={visibleColumns}
                      setVisibleColumns={setVisibleColumns}
                      columnOptions={columnOptions}
                      position="bottom-right" // Optional: adjust if you want bottom-left, etc.  
                    />
                  </div>
                </div>
                <div className="overflow-x-auto">
                  {activityLoading ? (
                    <div className="flex justify-center items-center py-12">
                      <Loader />
                    </div>
                  ) : (
                    <ReusableTable
                      columns={[
                        { key: 'name', label: 'Account Name', sortable: true },
                        { key: 'adminName', label: 'Admin Name', sortable: true },
                        { key: 'total', label: 'Activity Count', sortable: true },
                        { key: 'mostUsedFeature', label: 'Most Used Feature', sortable: false },
                        { key: 'mostUsedCount', label: 'Feature Count', sortable: true },
                      ]}
                      data={showMostUsed ? mostUsed : leastUsed}
                      visibleColumns={visibleColumns}
                      // onColumnToggle={handleColumnToggle}
                      // onSelectAllColumns={handleSelectAllColumns}
                      // onUnselectAllColumns={handleUnselectAllColumns}
                      noDataMessage={showMostUsed ? "No accounts with high activity" : "No accounts with low activity"}
                      truncateLength={11} // Matches your original truncation
                    />
                  )}
                </div>
              </div>

              <div
                className="glass-effect p-4 rounded-2xl border border-purple-500/30 w-full md:w-[630px] flex-shrink-0"
                style={{
                  width: "620 px",
                  height: "385px",
                  borderRadius: "6px",
                  transform: "rotate(0deg)",
                  opacity: 1,
                  background:
                    "linear-gradient(252.82deg, rgba(217, 217, 217, 0.025) 25.41%, rgba(115, 108, 207, 0.005) 115.2%)",
                  backdropFilter: "blur(75px)",
                  border: "0px solid",
                  position: "relative",
                  zIndex: 10,
                }}
              >
                <div className="flex justify-between items-center mb-6">
                  <h3
                    className="text-lg font-medium text-[#D3B4FF]"
                    style={{
                      fontFamily: "Inter",
                      fontWeight: 300,
                      fontStyle: "Light",
                      fontSize: "16px",
                      lineHeight: "100%",
                      letterSpacing: "1%",
                      opacity: 1,
                      transform: "rotate(0deg)",
                      whiteSpace: "nowrap",
                      marginTop: "-10px",
                    }}
                  >
                    Account Activity Tracking{" "}
                    {selectedOrg !== "All Organizations"
                      ? ` - ${selectedOrg.length <= 15
                        ? selectedOrg
                        : `${selectedOrg.slice(0, 12)}...`
                      }`
                      : ""}
                  </h3>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => {
                        if (selectedOrg === "All Organizations") {
                          setShowOrgWarning(true);
                          return;
                        }
                        if (selectedOrgId) {
                          navigate(
                            `/geniehireadmin/organisation/${selectedOrgId}`
                          );
                        } else {
                          console.error(
                            "Organization ID not found for the selected organization."
                          );
                        }
                      }}
                      disabled={selectedOrg === "All Organizations"}
                      className={`flex items-center gap-2 px-4 py-2 border rounded-full text-[#CCCCCC] transition-colors duration-200 ${selectedOrg === "All Organizations"
                          ? "bg-[#2A2A2A] border-[#5A4479] cursor-not-allowed opacity-50"
                          : "bg-transparent border-[#5A4479] hover:bg-[#2A2A2A]"
                        }`}
                    >
                      <span>View Detail</span>
                    </button>
                    <div className="relative">
                      <div ref={jobButtonRef}>
                        <FilterButton
                          filterCount={jobFilterCount}
                          onClick={() => setJobIsFilterOpen(!jobIsFilterOpen)}
                          onClear={() => {
                            setJobTempDateRange([null, null]);
                            setJobDateRange([null, null]);
                            setSelectedDatePeriod("weekly");
                            setSelectedFeature("All");
                            setSelectedOrg("All Organizations");
                          }}
                        />
                      </div>

                      <FilterPanel
                        ref={jobFilterRef}
                        isOpen={jobIsFilterOpen}
                        onClose={() => setJobIsFilterOpen(false)}
                        activeFilters={jobActiveFilters}
                        selectedPeriod={selectedDatePeriod}
                        onPeriodChange={setSelectedDatePeriod}
                        tempDateRange={jobTempDateRange}
                        onTempDateChange={setJobTempDateRange}
                        onApply={() => {
                          setJobDateRange(jobTempDateRange);
                          setJobIsFilterOpen(false);
                        }}
                        onReset={() => {
                          setJobTempDateRange([null, null]);
                          setJobDateRange([null, null]);
                          setSelectedDatePeriod("weekly");
                          setSelectedFeature("All");
                          setSelectedOrg("All Organizations");
                        }}
                        extraContent={
                          <>
                            {/* ==================== FEATURE DROPDOWN ==================== */}
                            <div className=" mb-4">
                              <label className="block text-[#CCCCCC] text-sm font-medium mb-2">
                                Feature
                              </label>

                              <div className="relative">
                                <button
                                  type="button"
                                  onClick={() => setFeatureOpen(!featureOpen)}
                                  className="w-full flex justify-between items-center px-3 py-2 bg-[#0a1733] border border-[#5A4479] rounded-md text-[#D3B4FF] text-sm hover:border-[#7E57C2] transition-colors"
                                >
                                  <span className="truncate">
                                    {selectedFeature === "All" ? "All" : selectedFeature}
                                  </span>
                                  {featureOpen ? (
                                    <ChevronUp className="w-4 h-4 text-[#D3B4FF]" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4 text-[#D3B4FF]" />
                                  )}
                                </button>

                                {featureOpen && (
                                  <div className="absolute z-50 mt-1 w-full left-0 bg-[#0a1733] border border-[#5A4479] rounded-md shadow-lg">
                                    {[
                                      { value: "All", label: "All" },
                                      { value: "Job Postings", label: "Job Postings" },
                                      { value: "Interview Scheduled", label: "Interview Scheduled" },
                                      { value: "Resume Parsed", label: "Resume Parsed" },
                                    ].map((opt) => (
                                      <div
                                        key={opt.value}
                                        onClick={() => {
                                          setSelectedFeature(opt.value);
                                          setFeatureOpen(false);
                                        }}
                                        className="px-3 py-2 text-sm text-[#D3B4FF] hover:bg-[#2A2A2A] cursor-pointer first:rounded-t-md last:rounded-b-md"
                                      >
                                        {opt.label}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* ==================== ORGANIZATION DROPDOWN ==================== */}
                            <div className=" mb-4">
                              <label className="block text-[#CCCCCC] text-sm font-medium mb-2">
                                Organization
                              </label>

                              <div className="relative">
                                <button
                                  type="button"
                                  onClick={() => setOrgOpen(!orgOpen)}
                                  className="w-full flex justify-between items-center px-3 py-2 bg-[#0a1733] border border-[#5A4479] rounded-md text-[#D3B4FF] text-sm hover:border-[#7E57C2] transition-colors"
                                >
                                  <span className="truncate">
                                    {selectedOrg === "All Organizations" ? "All Organizations" : selectedOrg}
                                  </span>
                                  {orgOpen ? (
                                    <ChevronUp className="w-4 h-4 text-[#D3B4FF]" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4 text-[#D3B4FF]" />
                                  )}
                                </button>

                                {orgOpen && (
                                  <div className="absolute z-50 mt-1 w-full left-0 bg-[#0a1733] border border-[#5A4479] rounded-md shadow-lg max-h-60 overflow-y-auto">
                                    <div
                                      onClick={() => {
                                        setSelectedOrg("All Organizations");
                                        setOrgOpen(false);
                                      }}
                                      className="px-3 py-2 text-sm text-[#D3B4FF] hover:bg-[#2A2A2A] cursor-pointer"
                                    >
                                      All Organizations
                                    </div>
                                    {availableOrgsForJobChart.map((org) => (
                                      <div
                                        key={org}
                                        onClick={() => {
                                          setSelectedOrg(org);
                                          setOrgOpen(false);
                                        }}
                                        className="px-3 py-2 text-sm text-[#D3B4FF] hover:bg-[#2A2A2A] cursor-pointer"
                                      >
                                        {org}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </>
                        }
                        position="bottom-right"
                        dropdownOpen={isDateDropdownOpen}
                        setDropdownOpen={setIsDateDropdownOpen}
                      />
                    </div>
                  </div>
                </div>
                {genieLoading ? (
                  <div style={{ height: '280px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Loader />
                  </div>
                ) : (
                  <LineChart
                    dataSources={[
                      { key: 'Job Postings', data: jobDailyData },
                      { key: 'Interviews Scheduled', data: interviewDailyData },
                      { key: 'Resumes Parsed', data: resumeDailyData },
                    ]}
                    selectedFeature={selectedFeature}
                    chartTheme={chartTheme}
                    height={280}
                    width="580px"
                  />
                )}
                <div className="flex justify-between items-center -mt-6 text-[#D3B4FF] text-sm font-semibold mx-auto ml-8">
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: COLORS[3] }}
                    ></div>
                    <span>Job Postings</span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: COLORS[4] }}
                    ></div>
                    <span>Interviews Scheduled</span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: COLORS[5] }}
                    ></div>
                    <span>Resumes Parsed</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Accounts Expiring Soon (Pie Chart) - Now on the Left */}
      <div
        className="glass-effect p-4 rounded-4xl w-[40%]"
        style={{
          background:
            "linear-gradient(252.82deg, rgba(217, 217, 217, 0.025) 25.41%, rgba(115, 108, 207, 0.005) 115.2%)",
        }}
      >
        <div className="flex justify-between items-center mb-2">
          <h3
            className="text-lg font-medium text-[#D3B4FF]"
            style={{
              fontFamily: "Inter",
              fontWeight: 300,
              fontSize: "16px",
              lineHeight: "100%",
              letterSpacing: "1%",
              marginTop: "7px",
              marginLeft: "10px",
            }}
          >
            Accounts Expiring Soon
          </h3>
        </div>

        <div className="flex items-center justify-between p-1">
          {/* Bubble Chart */}
          <ExpiringAccountsBubbleChart
            segments={expiringSegments}
            onSegmentClick={(segmentKey) => {
              setSelectedSegment(segmentKey);
              setIsModalOpen(true);
            }}
          />
          {/* LEGEND */}
          <div className="flex flex-col space-y-3 ml-4">
            {[
              { name: "0-7", value: expiringSegments[0]?.value || 0 },
              { name: "8-15", value: expiringSegments[1]?.value || 0 },
              { name: "16-30", value: expiringSegments[2]?.value || 0 },
            ].map((entry, index) => (
              <div key={index} className="flex items-center space-x-2">
                <div
                  className="w-4 h-4 rounded-full shadow-sm"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <span className="text-[#D3B4FF] text-sm font-medium min-w-16">
                  {entry.name} days
                </span>
                <span className="text-[#D3B4FF] text-sm">{entry.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal for Expiring Admins Details */}
      {isModalOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 overflow-hidden"
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            style={{
              backdropFilter: "blur(100px)",
              WebkitBackdropFilter: "blur(18px)",
            }}
          ></div>
          <div
            className="w-full max-w-4xl p-6 rounded-lg shadow-lg relative max-h-[80vh] overflow-y-auto"
            style={{
              background:
                "linear-gradient(to bottom right, rgba(34,52,97,0.25), rgba(11,10,31,0.05))",
              backdropFilter: "blur(40px)",
              WebkitBackdropFilter: "blur(50px)",
              borderRadius: "10px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-[#D3B4FF]">
                Accounts Expiring Soon{" "}
                {selectedSegment ? `- ${selectedSegment}` : ""}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-[#CCCCCC] hover:text-[#D3B4FF] transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <ReusableTable
                columns={[
                  {
                    key: 'org_admin_name',
                    label: 'Admin Name',
                    sortable: true,
                    render: (value) => value || 'N/A'
                  },
                  {
                    key: 'account_type',
                    label: 'Account Type',
                    sortable: true,
                    render: (value) => value || 'N/A'
                  },
                  {
                    key: 'end_date',
                    label: 'End Date',
                    sortable: true,
                    render: (date) => date ? formatDate(date) : 'N/A'
                  },
                  {
                    key: 'days_remaining',
                    label: 'Days Remaining',
                    sortable: true,
                    render: (_, row) => {
                      const days = getDaysRemaining(row.end_date);
                      return (
                        <span className={getDaysRemainingColor(days)}>
                          {days === "N/A" ? "N/A" : days}
                        </span>
                      );
                    }
                  },
                ]}
                data={[...filteredAdmins].sort((a, b) =>
                  getDaysRemaining(a.end_date) - getDaysRemaining(b.end_date)
                )}
                visibleColumns={{
                  org_admin_name: true,
                  account_type: true,
                  end_date: true,
                  days_remaining: true,
                }}
                noDataMessage="No accounts expiring in the selected period."
                truncateLength={20}
              />
            </div>

            {expiringAdmins.length === 0 && (
              <div className="text-center py-8 text-[#CCCCCC]">
                No accounts expiring in the next 30 days.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal for Bar Chart Details */}
      {/* BAR CHART MODAL – FINAL FIXED VERSION */}
      {isBarModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setIsBarModalOpen(false)}
        >
          <div className="fixed inset-0 bg-black/80 backdrop-blur-xl"></div>

          <div
            className="relative w-full max-w-4xl bg-gradient-to-br from-[#0f1a3b] to-[#0d1025] rounded-2xl shadow-2xl overflow-hidden border border-purple-900/50"
            onClick={(e) => e.stopPropagation()}
            style={{ maxHeight: '80vh' }} // Reduce height
          >
            <button
              onClick={() => setIsBarModalOpen(false)}
              className="absolute top-6 right-6 text-gray-400 hover:text-white z-10"
            >
              <X className="w-8 h-8" />
            </button>

            <div className="p-4"> {/* reduced padding from p-6 to p-4 */}
              <h2 className="text-xl md:text-2xl text-[#D3B4FF] mb-4 text-center"> {/* reduced mb-6 → mb-4 */}
                Daily Account Creation — {formatDate(chartDateRange[0])} to {formatDate(chartDateRange[1])}
              </h2>

              <div className="overflow-x-auto">
                <div
                  style={{
                    minWidth: '600px',
                    width: `${Math.max(600, dailyData.length * 35)}px`,
                  }}
                >
                  <CustomBarChart
                    data={dailyData}
                    xKey="date"
                    bars={[
                      { dataKey: "trial", fill: "#ad3bdd", name: "Trial Accounts" },
                      { dataKey: "paid", fill: "#B282F9", name: "Paid Accounts" }
                    ]}
                    height={450}
                    xAngle={-45}
                    xTextAnchor="end"
                    xHeight={100}
                    chartTheme={chartTheme}
                  />
                </div>
              </div>

              <div className="flex justify-center gap-6 -mt-20 text-[#D3B4FF]"> {/* mt-6 → mt-4, gap-8 → gap-6 */}
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-[#ad3bdd] rounded-full"></div>
                  <span>Trial Accounts</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-[#B282F9] rounded-full"></div>
                  <span>Paid Accounts</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardContent;